import React, { useState } from 'react';

interface Appt { id:number; title:string; contact:string; date:string; time:string; type:string; status:'Scheduled'|'Completed'|'Cancelled'; notes:string; }

const MOCK: Appt[] = [
  {id:1,title:'Life Insurance Consultation',contact:'Rivera Family',date:'2026-04-14',time:'10:00',type:'In-Person',status:'Scheduled',notes:'Interested in whole life'},
  {id:2,title:'Commercial Auto Review',contact:'ABC Logistics',date:'2026-04-14',time:'14:00',type:'Phone',status:'Scheduled',notes:'Review current coverage limits'},
  {id:3,title:'Renewal Discussion',contact:'Johnson Family',date:'2026-04-15',time:'11:30',type:'Video',status:'Scheduled',notes:'Home policy renewal due May 1'},
  {id:4,title:'New Client Intro',contact:'Davis LLC',date:'2026-04-13',time:'09:00',type:'Phone',status:'Completed',notes:'Referred by Marcus Williams'},
];

const typeColor: Record<string,string> = { 'In-Person':'bg-blue-100 text-blue-700','Phone':'bg-green-100 text-green-700','Video':'bg-purple-100 text-purple-700' };
const statusColor: Record<string,string> = { Scheduled:'bg-yellow-100 text-yellow-700', Completed:'bg-green-100 text-green-700', Cancelled:'bg-red-100 text-red-700' };

const AppointmentScheduler: React.FC = () => {
  const [appts, setAppts] = useState<Appt[]>(MOCK);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('Upcoming');
  const [form, setForm] = useState({ title:'', contact:'', date:'', time:'09:00', type:'Phone', notes:'' });

  const today = new Date().toISOString().split('T')[0];
  const filtered = filter==='Upcoming' ? appts.filter(a=>a.date>=today&&a.status==='Scheduled')
    : filter==='Completed' ? appts.filter(a=>a.status==='Completed') : appts;

  const add = () => {
    if (!form.title||!form.contact||!form.date) return;
    setAppts(p=>[...p,{...form,id:Date.now(),status:'Scheduled'}]);
    setForm({title:'',contact:'',date:'',time:'09:00',type:'Phone',notes:''});
    setShowModal(false);
  };

  const complete = (id:number) => setAppts(p=>p.map(a=>a.id===id?{...a,status:'Completed'}:a));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-900">Appointments ({filtered.length})</h1>
        <button onClick={()=>setShowModal(true)} className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">+ Schedule</button>
      </div>
      <div className="flex gap-1 bg-gray-100 p-1 rounded-lg w-fit">
        {['Upcoming','Completed','All'].map(f=>(
          <button key={f} onClick={()=>setFilter(f)} className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${filter===f?'bg-white shadow text-gray-900':'text-gray-600'}`}>{f}</button>
        ))}
      </div>
      <div className="space-y-3">
        {filtered.map(a=>(
          <div key={a.id} className="bg-white rounded-xl border border-gray-200 p-4 flex gap-4">
            <div className="flex-shrink-0 w-14 text-center">
              <p className="text-xs text-gray-500">{new Date(a.date+'T00:00').toLocaleString('en',{month:'short'})}</p>
              <p className="text-2xl font-bold text-gray-900">{new Date(a.date+'T00:00').getDate()}</p>
              <p className="text-xs text-blue-600 font-medium">{a.time}</p>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2 flex-wrap">
                <p className="text-sm font-semibold text-gray-900">{a.title}</p>
                <div className="flex gap-2">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${typeColor[a.type]}`}>{a.type}</span>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusColor[a.status]}`}>{a.status}</span>
                </div>
              </div>
              <p className="text-sm text-gray-600 mt-0.5">{a.contact}</p>
              {a.notes && <p className="text-xs text-gray-400 mt-1 italic">{a.notes}</p>}
            </div>
            {a.status==='Scheduled' && (
              <button onClick={()=>complete(a.id)} className="flex-shrink-0 px-3 py-1.5 text-xs text-green-700 bg-green-50 rounded-lg hover:bg-green-100">Mark Done</button>
            )}
          </div>
        ))}
      </div>
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-xl">
            <h2 className="text-lg font-semibold mb-4">Schedule Appointment</h2>
            <div className="space-y-3">
              {[['title','Title'],['contact','Contact / Client']].map(([k,l])=>(
                <input key={k} placeholder={l} value={(form as any)[k]} onChange={e=>setForm(p=>({...p,[k]:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
              ))}
              <div className="grid grid-cols-2 gap-3">
                <input type="date" value={form.date} onChange={e=>setForm(p=>({...p,date:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                <input type="time" value={form.time} onChange={e=>setForm(p=>({...p,time:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              </div>
              <select value={form.type} onChange={e=>setForm(p=>({...p,type:e.target.value}))} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm">
                <option>Phone</option><option>Video</option><option>In-Person</option>
              </select>
              <textarea placeholder="Notes (optional)" value={form.notes} onChange={e=>setForm(p=>({...p,notes:e.target.value}))} rows={2} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-500" />
            </div>
            <div className="flex gap-3 mt-4">
              <button onClick={add} className="flex-1 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">Schedule</button>
              <button onClick={()=>setShowModal(false)} className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 text-sm rounded-lg">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
export default AppointmentScheduler;
